"use client";
import { useState } from "react";
import ShoppingCart from "lucide-react/dist/esm/icons/shopping-cart";
import Heart from "lucide-react/dist/esm/icons/heart";
import Download from "lucide-react/dist/esm/icons/download";
import Zap from "lucide-react/dist/esm/icons/zap";
import HardDrive from "lucide-react/dist/esm/icons/hard-drive";
import Clock from "lucide-react/dist/esm/icons/clock";
import Share2 from "lucide-react/dist/esm/icons/share-2";
import TrendingUp from "lucide-react/dist/esm/icons/trending-up";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useProfile } from "@/contexts/ProfileContext";
import { toast } from "sonner";
import { isOnSale, getSaleTimeRemaining } from "@/lib/sale-utils";
import {
  allowsFreeClaim,
  isFreeClaimable,
  isPwywAsset,
  pwywInputHint,
  pwywMinimum,
  resolvePwywAmount,
} from "@/lib/pwyw";
import { listingPrice } from "@/lib/listing-price";
import { AssetPrice } from "@/components/asset/AssetPrice";
import { DiscountBadge } from "@/components/asset/DiscountBadge";
import type { AssetDetail } from "@/hooks/useAssetDetail";
import { formatFileSize } from "@/lib/format";
import { trackAssetAnalyticsEvent } from "@/lib/analytics/events";
import { copyToClipboard } from "@/lib/clipboard";
import { SignUpFlowDialog } from "@/components/auth/SignUpFlowDialog";
import { PlusPriceTag } from "@/components/ui/plus-price-tag";
import { VatInclusiveNote } from "@/components/pricing/VatInclusiveNote";
import { useCurrency } from "@/contexts/CurrencyContext";
import { ChargeCurrencyNote } from "@/components/currency/ChargeCurrencyNote";
import { RatingPromptDialog } from "@/components/asset-detail/RatingPromptDialog";
import { TipDialog } from "@/components/asset-detail/TipDialog";
import { LicenseAgreementDialog } from "@/components/asset-detail/LicenseAgreementDialog";
import dynamic from "next/dynamic";
import { useAssetActions } from "@/hooks/useAssetActions";
import { useRecentDownloads } from "@/hooks/useRecentDownloads";
import { useAssetPurchaseStats } from "@/hooks/useAssetPurchaseStats";
import { UpgradePrompt } from "@/components/ui/upgrade-prompt";
import { useDailyDownloadCount } from "@/hooks/useDailyDownloadCount";
import { DailyDownloadBadge } from "@/components/asset-detail/DailyDownloadBadge";

// Paywall dialog only loads when a free-tier user actually hits a limit moment.
const DownloadLimitDialog = dynamic(
  () => import("@/components/asset-detail/DownloadLimitDialog"),
  { ssr: false },
);

interface AssetPurchaseCardProps {
  asset: AssetDetail;
  className?: string;
}

const HOT_DOWNLOAD_THRESHOLD = 10;

export function AssetPurchaseCard({
  asset,
  className,
}: AssetPurchaseCardProps) {
  const { profile } = useProfile();
  const [pwywAmount, setPwywAmount] = useState("");
  // Fixed prices render in the display currency (via AssetPrice); pay-what-you-
  // want amounts are typed and charged in USD, so those stay USD (with the
  // local figure beside).
  const { formatUsd, formatWithUsd, isUsd } = useCurrency();

  const isPwyw = isPwywAsset(asset);
  const pwywMin = pwywMinimum(asset);
  const pwywFree = allowsFreeClaim(asset);
  const pwywState = resolvePwywAmount(asset, pwywAmount);

  const assetPricing = {
    price: asset.price,
    sale_price: asset.sale_price,
    sale_starts_at: asset.sale_starts_at,
    sale_ends_at: asset.sale_ends_at,
  };
  const onSale = !asset.is_free && !isPwyw && isOnSale(assetPricing);
  // What a buyer pays now: 0 free, sale-aware for fixed, the minimum for PWYW.
  const effectivePrice = listingPrice(asset).amount;
  const saleTimeRemaining = getSaleTimeRemaining(asset.sale_ends_at);

  const actions = useAssetActions({ asset, effectivePrice });

  const { data: recentDownloads } = useRecentDownloads(asset.id);
  const { data: purchaseStats } = useAssetPurchaseStats(asset.id);
  const dailyDownload = useDailyDownloadCount();

  const isHot = (recentDownloads ?? 0) >= HOT_DOWNLOAD_THRESHOLD;

  const {
    access,
    isLoadingAccess,
    isAddingToCart,
    isBuyingNow,
    isDownloading,
    isInWishlist,
    isTogglingWishlist,
    showDownloadButton,
    showFreeDownloadButton,
    showPurchaseButtons,
    showTipButton,
    addToCart,
    buyNow,
    toggleWishlist,
    openDownloadDialog,
    confirmDownload,
    requestTip,
    handleExpressSuccess,
    showLicenseDialog,
    setShowLicenseDialog,
    licenseAgreed,
    setLicenseAgreed,
    showExpressSignUp,
    setShowExpressSignUp,
    signUpIntent,
    showRatingPrompt,
    setShowRatingPrompt,
    recommendations,
    showTipDialog,
    setShowTipDialog,
    showLimitBanner,
    limitDialogVariant,
    setLimitDialogVariant,
  } = actions;

  return (
    <>
      <Card className={className}>
        <CardContent className="p-4 space-y-3">
          {/* Social Proof — purchase & download stats */}
          <RecentPurchasesBadge
            purchaseStats={purchaseStats}
            recentDownloads={recentDownloads ?? 0}
            isHot={isHot}
          />

          {/* Trending badge */}
          {isHot && (
            <Badge variant="secondary" className="text-xs bg-warning/10 text-warning border-warning/20">🔥 Trending</Badge>
          )}

          {/* Price and Wishlist Row */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <AssetPrice asset={asset} className="text-2xl font-bold" strikeClassName="text-lg" />
                <DiscountBadge asset={asset} className="px-2.5 py-1 text-xs" />
              </div>
              {/* PWYW detail stays USD: it's the currency the amount is typed in. */}
              {isPwyw && asset.price > 0 && (
                <p className="text-xs text-muted-foreground">Suggested: {formatWithUsd(asset.price)}</p>
              )}
              {isPwyw && pwywMin > 0 && (
                <p className="text-xs text-muted-foreground">Minimum: {formatWithUsd(pwywMin)}</p>
              )}
              {onSale && saleTimeRemaining && (
                <p className="text-xs font-medium text-destructive flex items-center gap-1"><Clock className="h-3 w-3" /> Sale ends in {saleTimeRemaining}</p>
              )}
              {!asset.is_free && !isPwyw && effectivePrice > 0 && (
                <PlusPriceTag price={effectivePrice} />
              )}
              {/* The price above is VAT-inclusive for viewers we charge VAT; this
                  says what's in it. PWYW amounts are typed before VAT. */}
              {!asset.is_free && !isPwyw && effectivePrice > 0 && (
                <VatInclusiveNote net={effectivePrice} className="mt-0.5" />
              )}
              {isPwyw && <VatInclusiveNote net={0} pwyw className="mt-0.5" />}
              {!isUsd && !asset.is_free && !isPwyw && effectivePrice > 0 && (
                <ChargeCurrencyNote className="text-xs" />
              )}
              {access?.hasPurchased && !access?.isOwner && <p className="text-xs text-muted-foreground">You own this</p>}
              {access?.isOwner && <p className="text-xs text-muted-foreground">Your asset</p>}
              {(purchaseStats?.total_purchases ?? 0) > 5 && (
                <p className="text-xs text-muted-foreground">
                  {purchaseStats!.total_purchases?.toLocaleString()}{" "}
                  {/* The counter includes free claims, which are real
                      acquisitions but not purchases — say what they are. */}
                  {isFreeClaimable(asset) ? "downloads" : "purchases"}
                </p>
              )}
            </div>
            {!access?.isOwner && (
              <Button variant="ghost" size="icon" onClick={toggleWishlist} disabled={isTogglingWishlist} aria-pressed={isInWishlist} aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"} className="shrink-0">
                <Heart className={`h-5 w-5 ${isInWishlist ? "fill-destructive text-destructive" : ""}`} />
              </Button>
            )}
          </div>

          {/* PWYW amount input */}
          {showPurchaseButtons && isPwyw && (
            <div className="space-y-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">$</span>
                <Input
                  type="number"
                  min={pwywFree ? "0" : pwywMin}
                  step="0.01"
                  placeholder={pwywFree ? "0.00" : `Min ${formatUsd(pwywMin)}`}
                  value={pwywAmount}
                  onChange={(e) => setPwywAmount(e.target.value)}
                  aria-label="Pay what you want amount"
                  aria-invalid={pwywState.kind === "invalid"}
                  aria-describedby="pwyw-hint"
                  className="pl-7 pr-12 text-base font-medium"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-muted-foreground">USD</span>
              </div>
              <p
                id="pwyw-hint"
                className={`text-xs ${pwywState.kind === "invalid" ? "text-destructive" : "text-muted-foreground"}`}
              >
                {pwywState.kind === "invalid" ? pwywState.message : pwywInputHint(asset)}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2">
            {isLoadingAccess && (
              <Button className="w-full" size="lg" loading loadingLabel="Checking access">
                Checking access…
              </Button>
            )}

            {!isLoadingAccess && showDownloadButton && (
              <>
                <Button
                  className="w-full font-semibold"
                  size="lg"
                  onClick={openDownloadDialog}
                  loading={isDownloading}
                  loadingLabel="Preparing download"
                >
                  {!isDownloading && <Download className="mr-2 h-5 w-5" />}
                  {isDownloading ? "Preparing…" : "Download"}
                </Button>
                {asset.file_size && (
                  <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                    <HardDrive className="h-3 w-3" />{formatFileSize(asset.file_size)}
                  </p>
                )}
                <DailyDownloadBadge daily={dailyDownload} />
              </>
            )}

            {!isLoadingAccess && showFreeDownloadButton && (
              <>
                <Button
                  className="w-full bg-success text-success-foreground hover:bg-success/90 font-semibold"
                  size="lg"
                  onClick={openDownloadDialog}
                  loading={isDownloading}
                  loadingLabel="Preparing download"
                >
                  {!isDownloading && <Download className="mr-2 h-5 w-5" />}
                  {isDownloading ? "Preparing…" : "Download Free"}
                </Button>
                {asset.file_size && (
                  <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                    <HardDrive className="h-3 w-3" />{formatFileSize(asset.file_size)}
                  </p>
                )}
                <DailyDownloadBadge daily={dailyDownload} />
              </>
            )}

            {!isLoadingAccess && showPurchaseButtons && !isPwyw && (
              <>
                <Button
                  className="w-full h-12 font-semibold"
                  size="lg"
                  onClick={() => buyNow()}
                  disabled={isAddingToCart}
                  loading={isBuyingNow}
                  loadingLabel="Processing checkout"
                >
                  {!isBuyingNow && <Zap className="mr-2 h-5 w-5" />}
                  {isBuyingNow ? "Processing…" : "Buy Now"}
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-10"
                  size="default"
                  onClick={() => addToCart()}
                  disabled={isBuyingNow}
                  loading={isAddingToCart}
                  loadingLabel="Adding to cart"
                >
                  {!isAddingToCart && <ShoppingCart className="mr-2 h-4 w-4" />}
                  {isAddingToCart ? "Adding…" : "Add to Cart"}
                </Button>
              </>
            )}

            {/* $0 on a no-minimum PWYW asset is a free claim, not a purchase:
                same license dialog and download path as any free asset. */}
            {!isLoadingAccess && showPurchaseButtons && isPwyw && pwywState.kind === "free" && (
              <>
                <Button
                  className="w-full bg-success text-success-foreground hover:bg-success/90 font-semibold"
                  size="lg"
                  onClick={openDownloadDialog}
                  loading={isDownloading}
                  loadingLabel="Preparing download"
                >
                  {!isDownloading && <Download className="mr-2 h-5 w-5" />}
                  {isDownloading ? "Preparing…" : "Get it free"}
                </Button>
                <DailyDownloadBadge daily={dailyDownload} />
              </>
            )}

            {!isLoadingAccess && showPurchaseButtons && isPwyw && pwywState.kind !== "free" && (
              <>
                <Button
                  className="w-full font-semibold"
                  size="lg"
                  onClick={() => pwywState.kind === "paid" && buyNow(pwywState.amount)}
                  disabled={isAddingToCart || pwywState.kind !== "paid"}
                  loading={isBuyingNow}
                  loadingLabel="Processing checkout"
                >
                  {!isBuyingNow && <Zap className="mr-2 h-5 w-5" />}
                  {isBuyingNow
                    ? "Processing…"
                    : pwywState.kind === "paid"
                      ? `Buy for ${formatUsd(pwywState.amount)}`
                      : "Enter an Amount"}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  size="lg"
                  onClick={() => pwywState.kind === "paid" && addToCart(pwywState.amount)}
                  disabled={isBuyingNow || pwywState.kind !== "paid"}
                  loading={isAddingToCart}
                  loadingLabel="Adding to cart"
                >
                  {!isAddingToCart && <ShoppingCart className="mr-2 h-5 w-5" />}
                  {isAddingToCart ? "Adding…" : "Add to Cart"}
                </Button>
              </>
            )}

            {/* Tip button — shown when tipping is enabled and user has access */}
            {!isLoadingAccess && showTipButton && (
              <Button
                variant="outline"
                size="sm"
                className="w-full text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                onClick={requestTip}
              >
                <Heart className="mr-2 h-4 w-4 fill-current" />
                Tip the Creator
              </Button>
            )}
          </div>

          {/* Daily limit upgrade prompt — shown after hitting the daily free cap */}
          {showLimitBanner && (
            <UpgradePrompt
              feature="Daily downloads"
              current={dailyDownload.used}
              limit={dailyDownload.limit}
              requiredTier="buyer_plus"
              className="mt-1"
            />
          )}

          {/* Share Button */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground hover:text-foreground"
            onClick={() => {
              const base = `https://uefnmarketplace.com/asset/${asset.slug}`;
              const shareUrl = profile?.id ? `${base}?ref=${profile.id}` : base;
              copyToClipboard(shareUrl).then((ok) => {
                if (!ok) return;
                toast.success("Share link copied! Paste it on Discord, Twitter, etc. for a rich preview.");
                trackAssetAnalyticsEvent({ name: "asset_shared", payload: { asset_id: asset.id, channel: "copy" } });
              });
            }}
          >
            <Share2 className="mr-2 h-4 w-4" />
            Copy Share Link
          </Button>

          <p className="text-xs text-muted-foreground text-center pt-2 border-t">
            Instant download · Lifetime access · Free updates
          </p>
        </CardContent>
      </Card>

      {/* License Agreement Dialog (shared) */}
      <LicenseAgreementDialog
        open={showLicenseDialog}
        onOpenChange={setShowLicenseDialog}
        licenseType={asset.license_type}
        agreed={licenseAgreed}
        onAgreedChange={setLicenseAgreed}
        onConfirm={confirmDownload}
        idPrefix="downloadLicenseAgreement"
      />

      {/* Express Sign-Up Dialog for guests */}
      <SignUpFlowDialog
        open={showExpressSignUp}
        onOpenChange={setShowExpressSignUp}
        intent={signUpIntent}
        context={{
          title: asset.title,
          thumbnail: asset.images?.[0]?.image_url,
          isFree: asset.is_free,
          sellerId: asset.seller?.id,
        }}
        onSuccess={handleExpressSuccess}
      />

      {/* Rating Prompt + post-download recommendations */}
      <RatingPromptDialog
        open={showRatingPrompt}
        onOpenChange={setShowRatingPrompt}
        assetId={asset.id}
        assetTitle={asset.title}
        recommendations={recommendations}
      />

      {/* Tip Dialog */}
      <TipDialog
        open={showTipDialog}
        onOpenChange={setShowTipDialog}
        assetId={asset.id}
        assetTitle={asset.title}
        sellerName={asset.seller?.display_name || asset.seller?.username || "Creator"}
      />

      {/* Download-limit paywall / soft-wall interstitial */}
      {limitDialogVariant && (
        <DownloadLimitDialog
          open
          onOpenChange={(next) => { if (!next) setLimitDialogVariant(null); }}
          variant={limitDialogVariant}
          used={dailyDownload.used}
        />
      )}
    </>
  );
}

/* ── Social Proof Badge ────────────────────────────────────────────── */
import Flame from "lucide-react/dist/esm/icons/flame";
import Users from "lucide-react/dist/esm/icons/users";
import type { AssetPurchaseStats } from "@/hooks/useAssetPurchaseStats";

function RecentPurchasesBadge({
  purchaseStats,
  recentDownloads,
  isHot,
}: {
  purchaseStats: AssetPurchaseStats | undefined;
  recentDownloads: number;
  isHot: boolean;
}) {
  const p24h = purchaseStats?.purchases_last_24h ?? 0;
  const p7d = purchaseStats?.purchases_last_7d ?? 0;
  const p30d = purchaseStats?.purchases_last_30d ?? 0;

  // Show the most impressive stat
  if (p24h > 0) {
    return (
      <div className="flex items-center gap-1.5 text-xs font-medium text-warning bg-warning/10 rounded-md px-2.5 py-1.5">
        <Flame className="h-3.5 w-3.5" />
        <Users className="h-3.5 w-3.5" />
        {p24h} {p24h === 1 ? "person" : "people"} bought this in the last 24 hours
      </div>
    );
  }

  if (p7d > 0) {
    return (
      <div className="flex items-center gap-1.5 text-xs font-medium text-warning bg-warning/10 rounded-md px-2.5 py-1.5">
        <Users className="h-3.5 w-3.5" />
        {p7d} {p7d === 1 ? "purchase" : "purchases"} this week
      </div>
    );
  }

  if (isHot) {
    return (
      <div className="flex items-center gap-1.5 text-xs font-medium text-warning bg-warning/10 rounded-md px-2.5 py-1.5">
        <TrendingUp className="h-3.5 w-3.5" />
        {recentDownloads} downloads in the last 30 days
      </div>
    );
  }

  if (p30d > 0) {
    return (
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted/50 rounded-md px-2.5 py-1.5">
        <Users className="h-3.5 w-3.5" />
        {p30d} {p30d === 1 ? "purchase" : "purchases"} this month
      </div>
    );
  }

  return null;
}

